import {
  groupCustomFontsIntoFamilies,
  parseFontFamilies,
} from '@src/utility/fonts';
import {
  detectFontScriptCoverage,
  getUncoveredWorkspaceLanguages,
} from '@src/utility/fontScriptCoverage';
import { LogManager } from '@src/utility/Logger';
import { get, set } from 'lodash';
import { useObserver } from 'mobx-react-lite';
import { parse as parseFont } from 'opentype.js';
import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';

import { extractErrorInfo } from '../../../utility/error';
import { formatUploadToArtifact } from '../../../utility/general';

import type VideoCreatorStore from '../../../stores/VideoCreatorStore';
import type {
  Artifact,
  BrandingCustomFont,
  BrandingFontMetadata,
  FileData,
  FontFamily,
} from '../../../types.ts/story';
import type { AlbumRepository, AssetRepository } from '@src/repositories';
import type { LocalizedName } from 'opentype.js';

const logger = LogManager.getLogger('BrandKit:CustomFonts');

const mergeFontFamilies = (
  existing: FontFamily[],
  incoming: FontFamily[],
): FontFamily[] => {
  const result = [...existing];
  for (const newFamily of incoming) {
    const existingIdx = result.findIndex(f => f.name === newFamily.name);
    if (existingIdx >= 0) {
      const existingIds = new Set(
        result[existingIdx].variants.map(v => v.artifactId),
      );
      const newVariants = newFamily.variants.filter(
        v => !existingIds.has(v.artifactId),
      );
      result[existingIdx] = {
        ...result[existingIdx],
        variants: [...result[existingIdx].variants, ...newVariants],
      };
    } else {
      result.push(newFamily);
    }
  }
  return result;
};

declare module 'opentype.js' {
  interface FontNames {
    preferredFamily?: LocalizedName;
    preferredSubfamily?: LocalizedName;
  }
}

export const useFontDropzone = ({
  onDrop,
}: {
  onDrop: (acceptedFiles: File[]) => void;
}) => {
  const { getRootProps, getInputProps, open } = useDropzone({
    noClick: true,
    accept: {
      'font/ttf': ['.ttf'],
      'font/otf': ['.otf'],
      'font/woff': ['.woff'],
      'font/woff2': ['.woff2'],
      'application/x-font-ttf': ['.ttf'],
      'application/x-font-opentype': ['.otf'],
      'application/font-sfnt': ['.ttf', '.otf'],
      'application/font-woff': ['.woff'],
      'application/font-woff2': ['.woff2'],
    },
    onDrop,
  });
  return { getRootProps, getInputProps, open };
};

const useBrandKitFontUpload = (
  videoCreator: VideoCreatorStore,
  assetRepository: AssetRepository,
  albumRepository: AlbumRepository,
) => {
  // TODO: Store access.
  const organization = useObserver(() => videoCreator.organization);

  const [uploading, setUploading] = useState(false);
  const uploadFontsToBrandKit = useCallback(
    async (files: File[]) => {
      try {
        setUploading(true);

        if (!organization) {
          logger.error('Cannot upload fonts: organization is not set');
          return;
        }

        const requests = files.map(async file => {
          const fontData = await parseFontData(file);
          const artifact = await uploadFontArtifact(
            assetRepository,
            file,
            fontData.postScriptName,
          );
          return { artifact, fontData };
        });
        const results = await Promise.all(requests);

        const customFontFiles = results.map(result => result.artifact);
        set(organization, 'branding.customFontFiles', [
          ...(get(organization, 'branding.customFontFiles') || []),
          ...customFontFiles,
        ]);

        const customFonts = results.map(result =>
          buildCustomFont(result.artifact, result.fontData),
        );
        set(organization, 'branding.customFonts', [
          ...(get(organization, 'branding.customFonts') || []),
          ...customFonts,
        ]);

        const newFamilies = groupCustomFontsIntoFamilies(customFonts);
        const existingFamilies: FontFamily[] = parseFontFamilies(
          get(organization, 'branding.fontFamilies'),
        );
        set(
          organization,
          'branding.fontFamilies',
          mergeFontFamilies(existingFamilies, newFamilies),
        );

        await albumRepository?.update(organization);

        // PLAT-11412 — warn (never block) about workspace languages the
        // uploaded fonts can't display; the admin decides whether the
        // font's role makes coverage relevant. One toast per FAMILY (a
        // family's weights upload as several files) and a truncated
        // language list — toasts are one short line.
        const byFamily = new Map<string, BrandingFontMetadata[]>();
        for (const { fontData } of results) {
          const members = byFamily.get(fontData.fontFamily) ?? [];
          byFamily.set(fontData.fontFamily, [...members, fontData]);
        }
        for (const [familyName, members] of byFamily) {
          const uncovered = getUncoveredWorkspaceLanguages(
            members,
            organization.supportedLanguages,
          );
          if (uncovered.length) {
            const shown = uncovered.slice(0, 3).join(', ');
            const rest = uncovered.length - 3;
            videoCreator.showToast({
              state: 'warning',
              message: `${familyName} doesn't support ${shown}${rest > 0 ? ` and ${rest} more` : ''}`,
            });
          }
        }
      } catch (error) {
        logger.error('Custom font upload failed:', extractErrorInfo(error));
      } finally {
        setUploading(false);
      }
    },
    [organization],
  );

  const [archiving, setArchiving] = useState(false);
  const archiveFontFromBrandKit = useCallback(
    async (fontToArchive: BrandingCustomFont) => {
      try {
        setArchiving(true);
        const now = new Date().toISOString();
        const customFonts = get(organization, 'branding.customFonts') || [];
        const updated: BrandingCustomFont[] = customFonts.map(font =>
          font.artifactId === fontToArchive.artifactId
            ? { ...font, archivedAt: now }
            : font,
        );
        set(organization!, 'branding.customFonts', updated);

        const fontFamilies: FontFamily[] = parseFontFamilies(
          get(organization, 'branding.fontFamilies'),
        );
        set(
          organization!,
          'branding.fontFamilies',
          fontFamilies.map(family => ({
            ...family,
            variants: family.variants.map(v =>
              v.artifactId === fontToArchive.artifactId
                ? { ...v, archivedAt: now }
                : v,
            ),
          })),
        );

        await albumRepository?.update(organization!);
      } catch (error) {
        logger.error('Custom font archive failed:', extractErrorInfo(error));
      } finally {
        setArchiving(false);
      }
    },
    [organization],
  );

  return {
    uploadFontsToBrandKit,
    uploading,
    archiveFontFromBrandKit,
    archiving,
  };
};

export const parseFontData = async (file: File) => {
  const getFontNameString = (name: LocalizedName) =>
    name['en'] || Object.values(name)[0];

  const font = parseFont(await file.arrayBuffer());
  const fontFamily = font.names.preferredFamily || font.names.fontFamily;
  const fontSubfamily =
    font.names.preferredSubfamily || font.names.fontSubfamily;
  const fullName = font.names.fullName;
  const postScriptName = font.names.postScriptName;
  const usWeightClass = font.tables.os2.usWeightClass;
  return {
    fontFamily: getFontNameString(fontFamily),
    fontSubfamily: getFontNameString(fontSubfamily),
    fullName: getFontNameString(fullName),
    postScriptName: getFontNameString(postScriptName),
    usWeightClass,
    // PLAT-11412 — which caption scripts the file's cmap covers; persisted
    // with the font so Brand Kit and the caption picker can flag gaps.
    scriptCoverage: detectFontScriptCoverage(font),
  } as BrandingFontMetadata;
};

const uploadFontArtifact = async (
  assetRepository: AssetRepository | undefined,
  file: File,
  fontName: string,
) => {
  if (!assetRepository) {
    logger.error('Cannot upload font artifact: assetRepository is undefined');
    throw new Error('assetRepository is undefined');
  }

  const fileExtension = file.name.split('.')[1];
  const fileData: FileData & { fileName: string } = {
    type: 'artifact',
    file,
    fileName: `${fontName}.${fileExtension}`,
    title: `${fontName}.${fileExtension}`,
    alt: '',
  };
  const newUpload = await assetRepository.uploadFile(fileData);
  return formatUploadToArtifact(newUpload, {});
};

const buildCustomFont: (
  artifact: Artifact,
  fontData: BrandingFontMetadata,
) => BrandingCustomFont = (artifact, fontData) =>
  ({
    artifactId: artifact.id,
    format: artifact.format,
    archivedAt: null,

    // font metadata
    fontFamily: fontData.fontFamily,
    fontSubfamily: fontData.fontSubfamily,
    fullName: fontData.fullName,
    postScriptName: fontData.postScriptName,
    usWeightClass: fontData.usWeightClass,
    scriptCoverage: fontData.scriptCoverage,

    // creatomate specific properties
    style: getFontStyle(fontData.fontSubfamily),
    source: artifact.url,
  }) as BrandingCustomFont;

const getFontStyle = (fontSubfamily: string) =>
  fontSubfamily.toLowerCase().includes('italic') ? 'italic' : 'normal';

export default useBrandKitFontUpload;
